/**
 * The thread pane — V7-04 §2.
 *
 * Opened from a message's "N replies" or its thread button, and it is the only
 * place a reply is ever shown. That is the point: the room's scroll stays
 * readable at company scale because the back-and-forth happens here instead.
 *
 * It is a third column on desktop and a full-screen sheet on mobile, because a
 * thread on a phone next to a conversation next to a list is three panes in
 * 390 points and none of them usable.
 */

import { useRef, useState } from 'react';
import { Bell, BellOff, Loader2, Paperclip, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { UserAvatar } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import {
  uploadChatFile,
  useFollowThread,
  useReplyInThread,
  useThread,
  type ChatAttachmentView,
  type ChatMessageView,
} from './api';

export interface ThreadPaneProps {
  root: ChatMessageView;
  /** The room the thread is in — attachments are presigned against it. */
  conversationId: string;
  /** Names for the people in this conversation, so a reply can say who wrote it. */
  nameFor: (userId?: string) => string;
  avatarFor: (userId?: string) => string | undefined;
  currentUserId: string;
  /** False in an archived channel or announcements mode — §1. */
  canPost: boolean;
  /** Whether this person currently follows the thread — §2. */
  following: boolean;
  onClose: () => void;
}

export function ThreadPane({
  root,
  conversationId,
  nameFor,
  avatarFor,
  currentUserId,
  canPost,
  following,
  onClose,
}: ThreadPaneProps) {
  const [draft, setDraft] = useState('');
  /** §7 — drag a file anywhere in the pane, the same as the main composer. */
  const [pending, setPending] = useState<ChatAttachmentView[]>([]);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const replies = useThread(root._id);
  const reply = useReplyInThread();
  const follow = useFollowThread();

  async function attach(files: FileList | File[]): Promise<void> {
    const list = Array.from(files);
    if (list.length === 0) return;
    setUploading(true);
    try {
      for (const file of list) {
        const uploaded = await uploadChatFile(conversationId, file);
        setPending((current) => [...current, uploaded]);
      }
    } catch (error) {
      toast.error(messageOf(error));
    } finally {
      setUploading(false);
    }
  }

  const send = () => {
    const body = draft.trim();
    // §7 — a reply is text, attachments, or both, exactly like a message.
    if (!body && pending.length === 0) return;
    const attachments = pending.map((file) => file._id);
    setDraft('');
    setPending([]);
    reply.mutate(
      { rootId: root._id, body, attachments },
      {
        onError: (error) => {
          // Put it back: losing what somebody typed, or a file they waited for
          // to upload, is worse than the failure that caused it.
          setDraft(body);
          setPending(pending);
          toast.error(messageOf(error));
        },
      },
    );
  };

  return (
    <section
      className={cn(
        'flex w-full flex-col rounded-[10px] vt-surface transition-colors md:w-[340px]',
        dragging && 'bg-[var(--accent-soft)]',
      )}
      data-testid="thread-pane"
      data-root-id={root._id}
      aria-label="Thread"
      // §7 — "drag-drop anywhere in the thread pane", so the handlers are on
      // the pane rather than on the composer at the bottom of it.
      onDragOver={(event) => {
        if (!canPost) return;
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        if (!canPost) return;
        event.preventDefault();
        setDragging(false);
        void attach(event.dataTransfer.files);
      }}
    >
      <header className="flex items-center gap-1 border-b border-[var(--border)] px-3 py-2">
        <span className="flex-1 text-[13px] font-medium">Thread</span>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={following ? 'Unfollow thread' : 'Follow thread'}
          data-testid="thread-follow"
          data-following={following ? 'true' : 'false'}
          onClick={() =>
            follow.mutate(
              { rootId: root._id, follow: !following },
              { onError: (error) => toast.error(messageOf(error)) },
            )
          }
        >
          {following ? <Bell className="text-[var(--accent)]" /> : <BellOff />}
        </Button>
        <Button variant="ghost" size="icon-sm" aria-label="Close thread" data-testid="thread-close" onClick={onClose}>
          <X />
        </Button>
      </header>

      <div className="flex-1 overflow-y-auto px-3 py-2" data-testid="thread-messages">
        {/* The root, always — a thread read without what started it is a
            conversation joined halfway through. */}
        <ThreadMessage
          message={root}
          isMine={root.senderId === currentUserId}
          name={nameFor(root.senderId)}
          avatarUrl={avatarFor(root.senderId)}
          isRoot
        />

        <div className="my-2 flex items-center gap-2">
          <span className="h-px flex-1 bg-[var(--border)]" />
          <span className="text-[11px] vt-muted" data-testid="thread-reply-count">
            {(replies.data?.length ?? 0) === 0
              ? 'No replies yet'
              : `${replies.data!.length} ${replies.data!.length === 1 ? 'reply' : 'replies'}`}
          </span>
          <span className="h-px flex-1 bg-[var(--border)]" />
        </div>

        {replies.isLoading && (
          <p className="py-2 text-center text-[11px] vt-muted">Loading replies…</p>
        )}

        {replies.data?.map((message) => (
          <ThreadMessage
            key={message._id}
            message={message}
            isMine={message.senderId === currentUserId}
            name={nameFor(message.senderId)}
            avatarUrl={avatarFor(message.senderId)}
          />
        ))}
      </div>

      {canPost ? (
        <div className="border-t border-[var(--border)] p-2">
          {(pending.length > 0 || uploading) && (
            <div className="mb-1 flex flex-wrap gap-1" data-testid="thread-pending-attachments">
              {pending.map((file) => (
                <span
                  key={file._id}
                  data-testid="thread-pending-attachment"
                  className="rounded-[4px] bg-[var(--surface-2)] px-1.5 py-0.5 text-[11px]"
                >
                  {file.fileName}
                </span>
              ))}
              {uploading && <span className="text-[11px] vt-muted">Uploading…</span>}
            </div>
          )}

          <div className="flex items-end gap-1.5">
            <input
              ref={fileInput}
              type="file"
              multiple
              hidden
              data-testid="thread-file-input"
              onChange={(event) => {
                if (event.target.files) void attach(event.target.files);
                event.target.value = '';
              }}
            />
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Attach a file"
              data-testid="thread-attach"
              onClick={() => fileInput.current?.click()}
            >
              <Paperclip />
            </Button>
          <textarea
            rows={1}
            data-testid="thread-composer"
            aria-label="Reply in thread"
            className="max-h-28 min-h-[32px] flex-1 resize-none rounded-[8px] bg-[var(--surface-2)] px-2 py-1.5 text-[13px] outline-none"
            placeholder="Reply…"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                send();
              }
            }}
          />
            <Button
              size="sm"
              data-testid="thread-send"
              disabled={(!draft.trim() && pending.length === 0) || reply.isPending}
              onClick={send}
            >
              {reply.isPending ? <Loader2 className="size-3.5 animate-spin" /> : 'Send'}
            </Button>
          </div>
        </div>
      ) : (
        <p
          className="border-t border-[var(--border)] p-2 text-center text-[11px] vt-muted"
          data-testid="thread-readonly"
        >
          This conversation is read-only.
        </p>
      )}
    </section>
  );
}

function ThreadMessage({
  message,
  isMine,
  name,
  avatarUrl,
  isRoot,
}: {
  message: ChatMessageView;
  isMine: boolean;
  name: string;
  avatarUrl?: string;
  isRoot?: boolean;
}) {
  return (
    <div
      className={cn('flex gap-2 py-1', isRoot && 'pb-2')}
      data-testid={isRoot ? 'thread-root' : 'thread-reply'}
      data-message-id={message._id}
    >
      <UserAvatar id={message.senderId ?? ''} name={name} src={avatarUrl} size={20} />
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium">
          {isMine ? 'You' : name}
          {message.taskRef?.taskKey && (
            <span className="ml-1.5 vt-muted" data-testid="thread-task-chip">
              {message.taskRef.taskKey} · {message.taskRef.titleSnapshot}
            </span>
          )}
        </p>
        {message.body && (
          <p className="whitespace-pre-wrap break-words text-[13px]">{message.body}</p>
        )}
      </div>
    </div>
  );
}

function messageOf(error: unknown): string {
  const detail = (error as { response?: { data?: { error?: { message?: string } } } })?.response
    ?.data?.error?.message;
  return detail ?? 'Something went wrong.';
}
